import { useState } from 'react';
import { startAuthentication, startRegistration } from '@simplewebauthn/browser';
import {
  requestPasskeyAuthOptions,
  requestPasskeyRegistrationOptions,
  verifyPasskeyAuthentication,
  verifyPasskeyRegistration,
} from '../security/passkey/passkeyApi';

interface PasskeyOptions {
  navegador?: string;
  sistemaOp?: string;
  fingerprint?: string;
  nomeDispositivo?: string;
}

type PasskeyOptionsEnvelope = Record<string, any> & {
  options?: Record<string, any>;
};

function unwrapPasskeyOptions(optionsData: PasskeyOptionsEnvelope): Record<string, any> {
  return optionsData.options || optionsData;
}

function getErrorName(err: unknown): string | undefined {
  if (!err || typeof err !== 'object' || !('name' in err)) {
    return undefined;
  }

  const name = (err as { name?: unknown }).name;
  return typeof name === 'string' ? name : undefined;
}

function getSafeErrorMessage(err: unknown, fallback: string): string {
  const apiError = err as {
    message?: unknown;
    response?: {
      data?: {
        error?: { message?: unknown };
        message?: unknown;
      };
    };
  };

  const structuredMessage = apiError.response?.data?.error?.message;
  if (typeof structuredMessage === 'string' && structuredMessage.trim()) {
    return structuredMessage;
  }

  const rawMessage = apiError.response?.data?.message;
  if (Array.isArray(rawMessage)) {
    return rawMessage.join('. ');
  }
  if (typeof rawMessage === 'string' && rawMessage.trim()) {
    return rawMessage;
  }
  if (typeof apiError.message === 'string' && apiError.message.trim()) {
    return apiError.message;
  }

  return fallback;
}

/**
 * Hook compartilhado usePasskeyEnrollment.
 * Centraliza registro e autenticacao WebAuthn via helper canonico.
 */
export function usePasskeyEnrollment() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Registro de Passkey. O JWT explicito e preservado porque alguns fluxos de
   * ativacao usam token transitorio antes de persistir a sessao.
   */
  const registerPasskey = async (jwt: string, extra?: PasskeyOptions): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      const requestOptions = { bearerToken: jwt };
      const optionsData = await requestPasskeyRegistrationOptions(
        { nomeDispositivo: extra?.nomeDispositivo },
        requestOptions,
      );
      const rawOptions = unwrapPasskeyOptions(optionsData as PasskeyOptionsEnvelope);

      const registrationOptions = {
        ...rawOptions,
        authenticatorSelection: {
          ...(rawOptions.authenticatorSelection || {}),
          authenticatorAttachment: 'platform' as const,
          residentKey: 'required' as const,
          requireResidentKey: true,
          userVerification: 'required' as const,
        },
      };

      const attestation = await startRegistration({ optionsJSON: registrationOptions as any });

      await verifyPasskeyRegistration(
        attestation,
        {
          nomeDispositivo: extra?.nomeDispositivo,
          navegador: extra?.navegador,
          sistemaOp: extra?.sistemaOp,
          fingerprint: extra?.fingerprint,
        },
        requestOptions,
      );

      return true;
    } catch (err: unknown) {
      if (getErrorName(err) === 'NotAllowedError') {
        setError('O cadastro biometrico foi cancelado pelo usuario.');
      } else {
        setError(getSafeErrorMessage(err, 'Erro inesperado ao registrar chave de acesso.'));
      }
      return false;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Autenticacao via Passkey. O JWT explicito e preservado porque alguns
   * fluxos usam token transitorio antes de persistir a sessao.
   */
  const authenticatePasskey = async (jwt: string): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      const requestOptions = { bearerToken: jwt };
      const optionsData = await requestPasskeyAuthOptions(requestOptions);
      const rawOptions = unwrapPasskeyOptions(optionsData as PasskeyOptionsEnvelope);

      const authOptions = {
        ...rawOptions,
        allowCredentials: (rawOptions.allowCredentials || []).map((credential: any) => ({
          ...credential,
          transports: ['internal'] as const,
        })),
      };

      const assertion = await startAuthentication({ optionsJSON: authOptions as any });

      await verifyPasskeyAuthentication(assertion, requestOptions);

      return true;
    } catch (err: unknown) {
      if (getErrorName(err) === 'NotAllowedError') {
        setError('A verificacao biometrica foi cancelada pelo usuario.');
      } else {
        setError(getSafeErrorMessage(err, 'Erro inesperado ao realizar login biometrico.'));
      }
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    registerPasskey,
    authenticatePasskey,
    loading,
    error,
  };
}
